import React, { useState } from "react";
import { Info } from "lucide-react";
import { useUser } from "util/UserContext";
import IssueSearch from "components/Issues/IssueSearch";
import AddButton from "ui/AddButton";
import ModalOrDrawer from "ui/ModalOrDrawer";
import { createCompoundIssue } from "util/issueCreationUtils";
import { fetchIssueInfluences } from "util/graphData";
import { useIssueFromSuggestion } from "hooks/useIssueFromSuggestion";
import { Issue, Suggestion } from "util/constants";
import { formatGraphMessage, graphMessage, type GraphLocale } from "./graphCopy";
import { toast } from "sonner";

type InfluenceDirection = "influencer" | "influenced";

interface GraphAddInfluenceSurfaceProps {
  isOpen: boolean;
  onClose: () => void;
  direction: InfluenceDirection;
  /** The issue we're adding an influencer for (inbound) or influenced for (outbound). */
  centerIssue: Issue;
  excludedIssueIds: number[];
  onAdded: (newIssue: Issue) => void | Promise<void>;
  locale: GraphLocale;
}

const getWikipediaLink = (issue: Issue | Suggestion): string => {
  const properties = (issue as Issue).properties;
  if (properties?.is_custom_issue || properties?.subject_properties?.is_custom_issue) {
    return "";
  }
  if ("wikipedia_link" in issue && issue.wikipedia_link) return issue.wikipedia_link;
  if ("topic_wikipedia_link" in issue && issue.topic_wikipedia_link) return issue.topic_wikipedia_link;
  if ("cname" in issue && issue.cname) {
    const title = (issue.cname as string).replace(/_/g, " ");
    return `https://en.wikipedia.org/wiki/${encodeURIComponent(title)}`;
  }
  return "";
};

/**
 * Modal/Drawer surface for adding an influencer or influenced issue to the graph.
 * IssueSearch + AddButton layout, matching InfluencesModal pattern.
 */
const GraphAddInfluenceSurface: React.FC<GraphAddInfluenceSurfaceProps> = ({
  isOpen,
  onClose,
  direction,
  centerIssue,
  excludedIssueIds,
  onAdded,
  locale,
}) => {
  const { currentUser, setShowAuthModal } = useUser();
  const { ensureIssue } = useIssueFromSuggestion();
  const [selectedIssue, setSelectedIssue] = useState<Suggestion | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [actionMessage, setActionMessage] = useState<{
    text: string;
    status: "success" | "error";
  } | null>(null);
  const centerIssueName =
    centerIssue.name ||
    centerIssue.cname?.replace(/_/g, " ") ||
    graphMessage(locale, "unknownIssue");

  const title = graphMessage(
    locale,
    direction === "influencer"
      ? "addInfluenceTitleInfluencer"
      : "addInfluenceTitleInfluenced"
  );

  const handleAdd = async () => {
    if (isSubmitting) {
      return;
    }
    if (!currentUser) {
      setShowAuthModal(true);
      return;
    }
    if (!selectedIssue) {
      setActionMessage({
        text: graphMessage(locale, "selectIssueFirst"),
        status: "error",
      });
      return;
    }

    setIsSubmitting(true);
    setActionMessage(null);

    /**
     * Resolve the Wikipedia / existing-issue suggestion into a persisted Issue.
     * `ensureIssue` handles 409 conflicts by fetching the canonical issue by cname,
     * so we never end up passing a zombie Suggestion (no id, no resolved properties)
     * to the collection add step.
     */
    let issueToLink: Issue;
    try {
      const resolved = await ensureIssue(selectedIssue);
      if (!resolved?.id) {
        throw new Error("GRAPH_INFLUENCE_ISSUE_MISSING");
      }
      issueToLink = resolved;
    } catch (resolveErr) {
      console.error("Error resolving issue for graph influence add:", resolveErr);
      setActionMessage({
        text: graphMessage(locale, "influenceCreateError"),
        status: "error",
      });
      setIsSubmitting(false);
      return;
    }

    try {
      if (direction === "influencer") {
        await createCompoundIssue(
          issueToLink.cname || "",
          centerIssue.cname,
          getWikipediaLink(issueToLink),
          getWikipediaLink(centerIssue),
          issueToLink.properties?.subject_properties,
          centerIssue.properties?.subject_properties
        );
      } else {
        await createCompoundIssue(
          centerIssue.cname,
          issueToLink.cname || "",
          getWikipediaLink(centerIssue),
          getWikipediaLink(issueToLink),
          centerIssue.properties?.subject_properties,
          issueToLink.properties?.subject_properties
        );
      }

      toast.success(graphMessage(locale, "influenceAddedToast"));
      setSelectedIssue(null);
      await onAdded(issueToLink);
      onClose();
    } catch (err) {
      console.error("Error adding influence:", err);
      try {
        const influences = await fetchIssueInfluences(centerIssue.id, direction, 100);
        const alreadyExists = influences.issues.some((issue) => issue.id === Number(issueToLink.id));
        if (alreadyExists) {
          toast.success(graphMessage(locale, "influenceAlreadyVisibleToast"));
          setSelectedIssue(null);
          await onAdded(issueToLink);
          onClose();
          return;
        }
      } catch {
        /* fall through to surface generic message */
      }
      setActionMessage({
        text: graphMessage(locale, "influenceCreateError"),
        status: "error",
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const footer = (
    <div className="flex flex-col gap-2">
      <IssueSearch
        inputType="add"
        placeholder={graphMessage(
          locale,
          direction === "influencer" ? "searchInfluencers" : "searchInfluenced"
        )}
        excludeIssueIds={[centerIssue.id, ...excludedIssueIds]}
        excludeCompoundIssues
        onSelect={(s) => {
          setSelectedIssue(s);
          setActionMessage(null);
        }}
        className="w-full"
        prepopulateData={selectedIssue ?? undefined}
      />
      <AddButton
        tooltipTitle={graphMessage(
          locale,
          direction === "influencer" ? "addInfluencer" : "addInfluenced"
        )}
        buttonText={graphMessage(
          locale,
          direction === "influencer" ? "addInfluencer" : "addInfluenced"
        )}
        onClick={handleAdd}
        isLoading={isSubmitting}
        className={`w-full ${!selectedIssue && !isSubmitting ? "opacity-50 cursor-not-allowed" : ""}`}
      />
      {actionMessage && (
        <div
          className={`graph-add-influence-action-message flex items-center gap-1 text-sm ${
            actionMessage.status === "success"
              ? "text-green-600 dark:text-green-400"
              : "text-warning-red dark:text-warning-red-dark"
          }`}
          role={actionMessage.status === "success" ? "status" : "alert"}
        >
          <Info className="size-4 shrink-0" />
          <span>{actionMessage.text}</span>
        </div>
      )}
    </div>
  );

  return (
    <ModalOrDrawer
      isOpen={isOpen}
      onClose={onClose}
      title={title}
      footer={footer}
      stickyFooter={true}
      className="graph-add-influence-surface"
    >
      <p className="graph-add-influence-surface-hint text-sm text-eoe-text-sub dark:text-eoe-dark-text-sub">
        {formatGraphMessage(
          locale,
          direction === "influencer"
            ? "addInfluenceHintInfluencer"
            : "addInfluenceHintInfluenced",
          { issueName: centerIssueName }
        )}
      </p>
    </ModalOrDrawer>
  );
};

export default GraphAddInfluenceSurface;
